import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { PROFILE, IMAGES } from "@/content";
import { useLanguage } from "@/lang";

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const { copy } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-screen flex flex-col justify-end overflow-hidden">
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <img
          src={IMAGES.portrait}
          alt="Clive Bucher Portrait"
          className="w-full h-[120%] object-cover object-center grayscale contrast-125"
        />
        <div className="absolute inset-0 bg-black/55" />
        <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-[#050505] to-transparent" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-[1600px] mx-auto w-full px-6 md:px-12 pb-16 md:pb-24">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          data-testid="hero-tagline"
          className="text-xs md:text-sm uppercase tracking-[0.35em] text-zinc-400 mb-6"
        >
          {copy.hero.tagline}
        </motion.p>

        <h1 className="font-display tracking-tight leading-[0.85] text-[19vw] md:text-[13vw] lg:text-[11vw]">
          <Line delay={0.35}>{PROFILE.firstName}</Line>
          <Line delay={0.5}>
            <span className="italic font-light">{PROFILE.lastName}</span>
          </Line>
        </h1>

        <div className="mt-10 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            data-testid="hero-intro"
            className="max-w-md text-sm md:text-base text-zinc-300 leading-relaxed"
          >
            {copy.hero.intro}
          </motion.p>

          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.1 }}
            data-testid="hero-scroll-btn"
            onClick={() => {
              const lenis = window.__lenis;
              const el = document.getElementById("headshots");
              if (lenis && el) lenis.scrollTo(el);
              else el?.scrollIntoView({ behavior: "smooth" });
            }}
            className="group flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-zinc-400 hover:text-white transition-colors duration-300"
          >
            {copy.hero.discover}
            <span className="w-10 h-10 border border-white/20 flex items-center justify-center group-hover:border-white/60 group-hover:translate-y-1 transition-[border-color,transform] duration-300">
              <ArrowDown size={16} />
            </span>
          </motion.button>
        </div>
      </motion.div>
    </section>
  );
};
